import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import queryHooks from '../../common/queryHooks/index.ts';
import Paginator from '../../components/Paginator.tsx';
import { useNavigate } from 'react-router-dom';
import { useDebounceValue } from '../../common/useDebounce.ts';
import moment from 'moment/moment';

interface IProps {
  isNracSubscribed: boolean;
  customerLocationId: number;
}

const DismissedRecallSearch = ({
  isNracSubscribed,
  customerLocationId,
}: IProps) => {
  const navigate = useNavigate();

  const initFilters = () => ({
    dateReceivedStart: '',
    dateReceivedEnd: '',
    manufacturer: '',
    medName: '',
    ndc: '',
    lotNumber: '',
  });

  const pageSize = useMemo(() => 50, []);
  const [filters, setFilters] = useState(initFilters);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const debouncedFilters = useDebounceValue(filters, 500);
  const searchParams = useMemo(
    () => ({
      ...debouncedFilters,
      pageNumber,
      pageSize,
      customerLocationId,
    }),
    [debouncedFilters, pageNumber, pageSize, customerLocationId]
  );

  const {
    data: dismissedRecalls,
    refetch,
    isLoading: isFetching,
  } = queryHooks.recall.useReadManyDismissedSearch(
    searchParams
  );

  const { data: totalRows } =
    queryHooks.recall.useReadManyDismissedSearchCount(
      {
        ...debouncedFilters,
        customerLocationId,
      },
      {
        enabled: Boolean(dismissedRecalls?.length),
      }
    );

  const onChange = ({
    target: { id, value },
  }: ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, [id]: value }));
    setPageNumber(1);
  };

  useEffect(() => {
    if (!showAdvancedSearch) {
      setFilters(prev => ({
        ...prev,
        dateReceivedEnd: '',
        dateReceivedStart: '',
      }));
    }
  }, [showAdvancedSearch]);

  const goToDetailPage = (recallId: number) =>
    navigate(`/recalls/detail/${recallId}`);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => { // <<< CHANGED
    if (event.key === 'Enter') {
      refetch();
    }
  };

  if (!isNracSubscribed) return null;
  return (
    <div>
      <div className="row" style={{ marginBottom: '0px', marginTop: '8px' }}>
        <a
          href="/"
          className="right"
          onClick={event => {
            event.preventDefault();
            setShowAdvancedSearch(prev => !prev);
          }}
        >
          {showAdvancedSearch ? 'hide ' : 'show '} advanced search options
        </a>
      </div>
      <div className="row">
        <div onKeyDown={handleKeyDown} className="row">
          <div className="input-field col s12 m3">
            <input
              id="medName"
              type="text"
              value={filters.medName}
              onChange={onChange}
            />
            <label htmlFor="medName">Med Name</label>
          </div>
          <div className="input-field col s12 m3">
            <input
              id="manufacturer"
              type="text"
              value={filters.manufacturer}
              onChange={onChange}
            />
            <label htmlFor="manufacturer">Manufacturer Name</label>
          </div>
          <div className="input-field col s12 m3">
            <input
              id="ndc"
              type="text"
              value={filters.ndc}
              onChange={onChange}
            />
            <label htmlFor="ndc">NDC</label>
          </div>
        </div>
        {showAdvancedSearch ? (
          <>
            <div className="input-field col s12 m3">
              <input
                id="dateReceivedStart"
                type="date"
                value={filters.dateReceivedStart}
                onChange={onChange}
              />
              <label>Date Recalled Range Start</label>
            </div>
            <div className="input-field col s12 m3">
              <input
                id="dateReceivedEnd"
                type="date"
                value={filters.dateReceivedEnd}
                onChange={onChange}
              />
              <label htmlFor="dateReceivedEnd">Date Recalled Range End</label>
            </div>
          </>
        ) : null}
      </div>
      <div className="row">
        {isFetching ? (
          <div className="progress">
            <div className="indeterminate blue" />
          </div>
        ) : (
          <>
            <table className="highlight">
              <thead>
              <tr>
                <th>Med Name</th>
                <th>Manufacturer</th>
                <th>NDCs</th>
                <th>Recall Date</th>
                <th>Date Dismissed</th>
                <th>Dismissed By</th>
              </tr>
              </thead>
              <tbody>
              {dismissedRecalls?.length ? (
                dismissedRecalls.map(({ recallId, product, manufacturer, recallCodes, dateDismissed, dismissedBy, dateReceived }) => (
                  <tr
                    key={recallId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => goToDetailPage(recallId)}
                  >
                    <td style={{ padding: '4px' }}>{product}</td>
                    <td style={{ padding: '4px' }}>{manufacturer}</td>
                    <td style={{ padding: '4px' }}>
                      {recallCodes
                        .map(({ ndcWithDashes }) => ndcWithDashes)
                        .join(', ')}
                    </td>
                    <td style={{ padding: '4px' }}>{moment(dateReceived).format('MM/DD/YY')}</td>
                    <td style={{ padding: '4px' }}>{moment(dateDismissed).format('MM/DD/YY')}</td>
                    <td style={{ padding: '4px' }}>{dismissedBy}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ padding: '4px' }} colSpan={4}>
                    Your search did not return any results
                  </td>
                </tr>
              )}
              </tbody>
            </table>
            <Paginator
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              pageSize={pageSize}
              totalItems={totalRows?.count || 0}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DismissedRecallSearch;
